import { useTranslation as usePublicI18n } from "react-i18next";
import { publicText, publicTemplate } from "./publicText";
import React, { useEffect } from "react";
import { BulbOutlined, ClockCircleOutlined, ExperimentOutlined, LineChartOutlined, RiseOutlined } from "@ant-design/icons";
import PublicLayout from "./PublicLayout";
import { CASE03 } from "./caseStudyData";
import { formatNumber, Reveal, useDocumentTitle } from "./components/PageUtilities";
import { CaseNavigation, CaseStudyBreadcrumb, DataTable, Figure, KeyFinding, MethodGrid, MetricCard, ScientificNote, SectionHeader } from "./components/ScientificComponents";
import { PhaseRibbon, ScientificLineChart } from "./components/ScientificCharts";
import "./case03.css";
import { trackEvent } from "../FirebaseConfig";
function CaseStudy03() {
  usePublicI18n();
  useDocumentTitle(publicText("Case Study 03｜商業栽培トマト圃場での大量計測"));
  useEffect(() => {
    trackEvent("case_study_view", {
      case_study_id: "03"
    });
  }, []);
  const labels = CASE03.rows.map(row => row.timeLabel);
  const values = key => CASE03.rows.map(row => row[key]);
  const tableRows = CASE03.rows.map(row => ({
    ...row,
    leafMeanOpenRateText: row.leafMeanOpenRate.toFixed(1),
    pooledOpenRateText: row.pooledOpenRate == null ? "—" : row.pooledOpenRate.toFixed(1),
    allMedianText: row.allMedian.toFixed(2),
    openMedianText: row.openMedian.toFixed(2),
    ppfdText: row.ppfd.toFixed(0),
    photosynthesisText: row.photosynthesis.toFixed(2),
    temperatureText: row.temperature.toFixed(1),
    rhText: row.rh.toFixed(1),
    vpdText: row.vpd.toFixed(2),
    co2Text: row.co2.toFixed(0)
  }));
  const snapshotRows = CASE03.representativeSnapshots.map(row => ({
    ...row,
    openRateText: row.pooledOpenRate.toFixed(1),
    allMedianText: row.allMedian.toFixed(2),
    openMedianText: row.openMedian.toFixed(2)
  }));
  return <PublicLayout>
      <article className="science-case-page case-theme-03">
        <header className="science-case-hero science-case-hero--data case03-field-hero">
          <div className="science-case-hero__grid" />
          <div className="science-orb orb-b" />
          <div className="science-orb orb-c" />

          <div className="case-container">
            <CaseStudyBreadcrumb current="03" />

            <div className="science-case-hero__layout">
              <Reveal className="science-case-hero__copy">
                <div className="science-case-id">{publicText("CASE STUDY 03")}</div>
                <span className="science-case-domain">{publicText("FIELD PHYSIOLOGY / COMMERCIAL TOMATO GREENHOUSE")}</span>

                <h1>{publicText("\u5546\u696D\u683D\u57F9\u30C8\u30DE\u30C8\u5703\u5834\u3067\u3001")}<br />
                  <em>{publicText("\u671D\u306E\u6C17\u5B54\u3092\u5927\u91CF\u8A08\u6E2C\u3057\u305F\u3002")}</em>
                </h1>

                <p>{publicText(CASE03.lead)}</p>

                <div className="science-case-tags">
                  <span>{publicText(CASE03.field.date)}</span>
                  <span>{publicText(CASE03.field.window)}</span>
                  <span>{publicText("random leaf sampling")}</span>
                  <span>{publicText("Open / Closed")}</span>
                  <span>{publicText("aperture distribution")}</span>
                  <span>{publicText("photosynthesis")}</span>
                </div>

                <div className="science-case-hero__headline-result is-four case03-field-metrics">
                  <div>
                    <small>{publicText("SAMPLED LEAVES")}</small>
                    <strong>
                      {publicText(formatNumber(CASE03.totals.sampledLeaves, 0))}
                    </strong>
                  </div>
                  <div>
                    <small>{publicText("STOMATA")}</small>
                    <strong>{publicText(formatNumber(CASE03.totals.stomata, 0))}</strong>
                  </div>
                  <div>
                    <small>{publicText("FIELD WINDOW")}</small>
                    <strong>{publicText("\u7D04")}{publicText(CASE03.totals.durationHours.toFixed(1))}
                      <small>{publicText("h")}</small>
                    </strong>
                  </div>
                  <div>
                    <small>{publicText("THROUGHPUT")}</small>
                    <strong>
                      {publicText(formatNumber(CASE03.totals.throughputPerHour, 0))}
                      <small>{publicText("/h")}</small>
                    </strong>
                  </div>
                </div>
              </Reveal>

              <Reveal className="science-case-hero__media case03-hero-chart" delay={120}>
                <div className="case03-hero-chart__screen">
                  <div className="case03-hero-chart__topbar">
                    <span />
                    <span />
                    <span />
                    <small>{publicText("StomataScope / Field time-series")}</small>
                  </div>
                  <img src={CASE03.images.trend} alt={publicText("朝の開気孔率と光合成速度A'の時系列")} />
                </div>

                <div className="case03-hero-chart__badge is-first">
                  <small>{publicText("FIELD DATASET")}</small>
                  <strong>{publicText("14,563")}</strong>
                  <span>{publicText("stomata")}</span>
                </div>

                <div className="case03-hero-chart__badge is-last">
                  <small>{publicText("GAS EXCHANGE")}</small>
                  <strong>{publicText(CASE03.totals.gasMeasurements)}</strong>
                  <span>{publicText("measurements")}</span>
                </div>
              </Reveal>
            </div>
          </div>
        </header>

        <section className="science-section science-question-section">
          <div className="study-content">
            <SectionHeader index="01" eyebrow="STUDY QUESTION" title={publicText("朝の植物群では、Openになる気孔の割合と、Open後の開度はどう変化するのか。")} description={publicText(CASE03.question)} />

            <KeyFinding title={publicText("大量計測により、Open率と開度分布の両方が時間とともに変化することを確認した。")}>
              <p>{publicText("7\u3064\u306E\u6642\u9593\u5E2F\u306714,563\u6C17\u5B54\u3092\u89E3\u6790\u3059\u308B\u3068\u3001\u671D\u524D\u534A\u306B\u306FOpen\u306B\u306A\u308B\u6C17\u5B54\u304C\u5897\u3048\u3001 \u663C\u306B\u5411\u3051\u3066\u306F\u958B\u5EA6\u5206\u5E03\u5168\u4F53\u304C\u5927\u304D\u3044\u5074\u3078\u79FB\u52D5\u3057\u307E\u3057\u305F\u3002\u5149\u5408\u6210\u901F\u5EA6\u3082\u3001 \u8449\u5358\u4F4DOpen\u7387\u306E\u4E0A\u6607\u3068\u540C\u3058\u6642\u9593\u5E2F\u306B\u5897\u52A0\u3059\u308B\u50BE\u5411\u3092\u793A\u3057\u307E\u3057\u305F\u3002")}</p>
            </KeyFinding>
          </div>
        </section>

        <section className="science-section is-soft">
          <div className="case-container">
            <SectionHeader index="02" eyebrow="FIELD CAMPAIGN" title={publicText("296葉をサンプリングし、約5.5時間で14,563気孔をデータ化。")} description={publicText(`${CASE03.field.date} ${CASE03.field.window}。${CASE03.field.sampling}`)} />

            <Reveal className="science-metric-grid is-four">
              <MetricCard label={publicText("圃場計測総数")} value={formatNumber(CASE03.totals.sampledLeaves, 0)} unit="葉" caption={publicText("チラシ掲載のサンプリング総数")} tone="green" icon={<ExperimentOutlined />} />
              <MetricCard label={publicText("有効統合葉数")} value={formatNumber(CASE03.totals.analyzedLeaves, 0)} unit="葉" caption={publicText("1時間統合表に計上された葉数")} tone="blue" icon={<LineChartOutlined />} />
              <MetricCard label={publicText("検出気孔数")} value={formatNumber(CASE03.totals.stomata, 0)} unit="個" caption={publicText("7時間帯の合計")} tone="purple" icon={<LineChartOutlined />} />
              <MetricCard label={publicText("計測速度")} value={publicTemplate(215, [formatNumber(CASE03.totals.throughputPerHour, 0)])} unit="気孔/時" caption={publicText("14,563 ÷ 約5.5時間")} tone="orange" icon={<ClockCircleOutlined />} />
            </Reveal>

            <MethodGrid items={[{
            label: "実測時間",
            value: CASE03.field.window,
            note: "グラフは6時台〜12時台の時間帯で表示"
          }, {
            label: "サンプリング",
            value: "異なる葉をランダム取得",
            note: "同一葉の連続追跡ではない"
          }, {
            label: "気孔解析",
            value: "Open / Closed + Aperture",
            note: "Closedは全気孔中央値で0 µmとして扱う"
          }, {
            label: "併測データ",
            value: "PPFD・光合成・環境",
            note: publicTemplate(226, [CASE03.totals.gasMeasurements])
          }]} />

            <ScientificNote title={publicText("296葉と295葉の表示について")} tone="caution">
              <p>{publicText("\u516C\u958B\u30C1\u30E9\u30B7\u306B\u306F\u5703\u5834\u8A08\u6E2C\u7DCF\u6570\u3068\u3057\u3066296\u8449\u3068\u8A18\u8F09\u3055\u308C\u3066\u3044\u307E\u3059\u3002\u4E00\u65B9\u3001 \u73FE\u5728\u306E1\u6642\u9593\u7D71\u5408\u8868\u3067\u6642\u9593\u5E2F\u5225\u306B\u5408\u8A08\u3055\u308C\u308B\u6709\u52B9\u89E3\u6790\u8449\u6570\u306F295\u8449\u3067\u3059\u3002 \u672C\u30DA\u30FC\u30B8\u3067\u306F\u4E21\u8005\u3092\u533A\u5225\u3057\u3066\u8868\u793A\u3057\u3066\u3044\u307E\u3059\u3002\u516C\u958B\u524D\u306B\u3001\u6B8B\u308B1\u8449\u306E \u96C6\u8A08\u4E0A\u306E\u6271\u3044\u3092\u5143\u30C7\u30FC\u30BF\u3067\u6700\u7D42\u78BA\u8A8D\u3057\u3066\u304F\u3060\u3055\u3044\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section className="science-section is-dark">
          <div className="case-container">
            <SectionHeader index="03" eyebrow="REPRESENTATIVE SNAPSHOTS" title={publicText("6時台・9時台・12時台では、Open率36% → 61% → 96%。")} description={publicText("気孔数ベースの3時点集計です。")} />

            <div className="case03-snapshot-grid">
              {publicText(CASE03.representativeSnapshots.map(row => <Reveal className="case03-snapshot-card" key={row.time}>
                  <div className="case03-snapshot-card__head">
                    <span>{publicText(row.timeLabel)}</span>
                    <strong>{publicText(row.pooledOpenRate.toFixed(1))}{publicText("%")}</strong>
                    <small>{publicText("pooled Open rate")}</small>
                  </div>

                  <div className="case03-open-track">
                    <i style={{
                  width: `${row.pooledOpenRate}%`
                }} />
                  </div>

                  <dl>
                    <div>
                      <dt>{publicText("Open / Closed")}</dt>
                      <dd>
                        {publicText(formatNumber(row.open, 0))}{publicText(" / ")}{publicText(formatNumber(row.closed, 0))}
                      </dd>
                    </div>
                    <div>
                      <dt>{publicText("Detected")}</dt>
                      <dd>{publicText(formatNumber(row.stomata, 0))}</dd>
                    </div>
                    <div>
                      <dt>{publicText("\u5168\u6C17\u5B54\u4E2D\u592E\u5024")}</dt>
                      <dd>{publicText(row.allMedian.toFixed(2))}{publicText(" \xB5m")}</dd>
                    </div>
                    <div>
                      <dt>{publicText("Open\u306E\u307F\u4E2D\u592E\u5024")}</dt>
                      <dd>{publicText(row.openMedian.toFixed(2))}{publicText(" \xB5m")}</dd>
                    </div>
                  </dl>
                </Reveal>))}
            </div>

            <DataTable caption={publicText("Table 1. チラシ掲載3時点の気孔数ベース集計")} columns={[{
            key: "timeLabel",
            label: "時間帯"
          }, {
            key: "stomata",
            label: "全気孔数",
            align: "right"
          }, {
            key: "open",
            label: "Open",
            align: "right"
          }, {
            key: "closed",
            label: "Closed",
            align: "right"
          }, {
            key: "openRateText",
            label: "Open率",
            unit: "%",
            align: "right"
          }, {
            key: "allMedianText",
            label: "全気孔中央値",
            unit: "µm",
            align: "right"
          }, {
            key: "openMedianText",
            label: "Openのみ中央値",
            unit: "µm",
            align: "right"
          }]} rows={snapshotRows} />
          </div>
        </section>

        <section className="science-section">
          <div className="case-container">
            <SectionHeader index="04" eyebrow="LEAF-WEIGHTED OPEN RATE" title={publicText("葉単位Open率は、6時台24.3%から10時台94.9%へ。")} description={publicText("7時間帯の詳細トレンドは、各葉を分析単位として再集計したOpen率で表示します。")} />

            <Reveal>
              <ScientificLineChart labels={labels} series={[{
              name: "葉単位Open率",
              values: values("leafMeanOpenRate")
            }]} title={publicText("葉単位Open率の時系列")} subtitle={publicTemplate(240, [CASE03.field.window])} unit="%" yMin={0} yMax={100} valueDigits={1} footer="10時台以降は94.9〜95.9%で高い状態を維持しました。" />
            </Reveal>

            <Reveal className="science-time-cards">
              {publicText(CASE03.rows.map(row => <div key={row.time} className={row.time === "10:00" ? "is-transition" : ""}>
                  <span>{publicText(row.timeLabel)}</span>
                  <strong>{publicText(row.leafMeanOpenRate.toFixed(1))}{publicText("%")}</strong>
                  <small>{publicText(formatNumber(row.stomata, 0))}{publicText(" stomata")}</small>
                </div>))}
            </Reveal>
          </div>
        </section>

        <section className="science-section is-dark">
          <div className="case-container">
            <SectionHeader index="05" eyebrow="APERTURE DISTRIBUTION" title={publicText("朝前半はOpenになる気孔が増え、朝後半はOpen気孔の開度が広がる。")} description={publicText("Open率とOpen気孔の開度を分けて見ることで、植物群の変化を二段階で記述できます。")} />

            <div className="science-chart-grid">
              <Reveal>
                <ScientificLineChart labels={labels} series={[{
                name: "全気孔中央値",
                values: values("allMedian")
              }, {
                name: "Open気孔のみ中央値",
                values: values("openMedian")
              }]} title={publicText("全気孔とOpen気孔のみの開度")} subtitle={publicText("Closedを0 µmとして含むか否か")} unit="µm" yMin={0} yMax={4.8} valueDigits={2} footer="6〜8時台は全気孔中央値が0.00 µmでも、Open気孔のみでは約3.1 µmでした。" />
              </Reveal>

              <Reveal delay={100} className="science-phase-explanation">
                <span>{publicText("WHY TWO METRICS?")}</span>
                <h3>{publicText("\u201C\u4F55\u500B\u304C\u958B\u3044\u3066\u3044\u308B\u304B\u201D\u3068\u201COpen\u6C17\u5B54\u304C\u3069\u308C\u3060\u3051\u958B\u3044\u3066\u3044\u308B\u304B\u201D\u306F\u5225\u306E\u60C5\u5831\u3002")}</h3>
                <dl>
                  <div>
                    <dt>{publicText("Open\u7387")}</dt>
                    <dd>{publicText("\u6C17\u5B54\u96C6\u56E3\u306E\u3046\u3061\u3001Open\u3068\u5224\u5B9A\u3055\u308C\u305F\u5272\u5408\u3002")}</dd>
                  </div>
                  <div>
                    <dt>{publicText("\u5168\u6C17\u5B54\u4E2D\u592E\u5024")}</dt>
                    <dd>{publicText("Closed\u30920 \xB5m\u3068\u3057\u3066\u542B\u307F\u3001\u96C6\u56E3\u5168\u4F53\u306E\u72B6\u614B\u3092\u8868\u3059\u3002")}</dd>
                  </div>
                  <div>
                    <dt>{publicText("Open\u306E\u307F\u4E2D\u592E\u5024")}</dt>
                    <dd>{publicText("Open\u306B\u306A\u3063\u305F\u6C17\u5B54\u306E\u958B\u53E3\u5E45\u3092\u8868\u3059\u3002")}</dd>
                  </div>
                </dl>
              </Reveal>
            </div>

            <PhaseRibbon phases={[{
            time: "6時台–9時台",
            title: "Phase 1 / Recruitment",
            description: "葉単位Open率 24.3→69.4%。Openのみ中央値は約3.1 µm。ClosedからOpenになる気孔が増える段階。"
          }, {
            time: "10時台–12時台",
            title: "Phase 2 / Widening",
            description: "葉単位Open率は約95%で安定。Openのみ中央値は3.68→4.40 µm。Open気孔がさらに広がる段階。"
          }]} />
          </div>
        </section>

        <section className="science-section">
          <div className="case-container">
            <SectionHeader index="06" eyebrow="PHOTOSYNTHESIS & LIGHT" title={publicText("Open率の上昇に伴い、光合成速度も上昇する傾向。")} description={publicText("チラシと同じ結論を保ちつつ、詳細ページではPPFD・気温・VPD・CO₂も併記します。")} />

            <div className="science-chart-grid">
              <Reveal>
                <ScientificLineChart labels={labels} series={[{
                name: "光合成速度 A'",
                values: values("photosynthesis")
              }]} title={publicText("光合成速度の時系列")} subtitle={publicText("各時間帯の中央値")} unit="µmol CO₂ m⁻² s⁻¹" yMin={0} yMax={24} valueDigits={1} footer="10時台に21.9へ達し、11〜12時台も約20〜21で推移しました。" />
              </Reveal>

              <Reveal delay={100}>
                <ScientificLineChart labels={labels} series={[{
                name: "PPFD",
                values: values("ppfd")
              }]} title={publicText("PPFDの時系列")} subtitle={publicText("同時間帯の光環境")} unit="µmol m⁻² s⁻¹" yMin={0} yMax={650} valueDigits={0} footer="6時台11.8から12時台629.6へ上昇しました。" />
              </Reveal>
            </div>

            <Figure number="01" title={publicText("チラシと同じ開気孔率・光合成速度の時系列")} src={CASE03.images.trend} alt={publicText("朝の開気孔率と光合成速度A'の時系列")} contain caption={publicText("印刷済みチラシと同じく、緑の棒で葉単位Open率、青の折れ線で光合成速度A'の実測値を表示しています。因果関係を示すものではありません。")} className="science-figure--analysis" />

            <ScientificNote title={publicText("傾向と因果関係を区別する")} tone="caution">
              <p>{publicText("Open\u7387\u3068\u5149\u5408\u6210\u901F\u5EA6\u306F\u540C\u3058\u6642\u9593\u8EF8\u3067\u4E0A\u6607\u3057\u3001\u5225\u56F3\u306EPPFD\u3082\u4E0A\u6607\u3057\u307E\u3057\u305F\u304C\u3001 \u6C17\u6E29\u3001RH\u3001VPD\u3001CO\u2082\u3082\u540C\u6642\u306B\u5909\u5316\u3057\u3066\u3044\u307E\u3059\u3002\u3053\u306E\u89B3\u5BDF\u306F\u540C\u6642\u5909\u5316\u3092\u793A\u3059\u3082\u306E\u3067\u3042\u308A\u3001 \u5358\u4E00\u8981\u56E0\u306E\u56E0\u679C\u52B9\u679C\u3092\u8A3C\u660E\u3059\u308B\u3082\u306E\u3067\u306F\u3042\u308A\u307E\u305B\u3093\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section className="science-section is-soft case03-flyer-section">
          <div className="case-container">
            <SectionHeader index="07" eyebrow="PUBLIC SUMMARY FLYER" title={publicText("第32回国際園芸学会議(IHC2026) 展示会場で配布の日本語チラシ")} description={publicText("")} />

            <div className="case03-flyer-layout">
              <Reveal className="case03-flyer-preview">
                <a href={CASE03.images.flyerPdf} target="_blank" rel="noreferrer" aria-label={publicText("Case Study 03 日本語チラシPDFを開く")}>
                  <img src={CASE03.images.flyer} alt={publicText("Case Study 03 商業栽培トマト圃場での大量計測チラシ")} />
                </a>
              </Reveal>

              <Reveal className="case03-flyer-copy" delay={100}>
                <span>{publicText("FLYER SUMMARY")}</span>
                <h3>{publicText("\u5546\u696D\u683D\u57F9\u30C8\u30DE\u30C8\u5703\u5834\u3067\u306E\u5927\u91CF\u8A08\u6E2C\u306E\u5B9F\u7E3E")}</h3>
                <dl>
                  <div>
                    <dt>{publicText("\u6E2C\u5B9A\u898F\u6A21")}</dt>
                    <dd>{publicText("296\u8449 / 14,563\u6C17\u5B54 / \u7D045.5\u6642\u9593")}</dd>
                  </div>
                  <div>
                    <dt>{publicText("\u8A08\u6E2C\u901F\u5EA6")}</dt>
                    <dd>{publicText("\u7D042,647\u6C17\u5B54 / \u6642\u9593")}</dd>
                  </div>
                  <div>
                    <dt>{publicText("\u4EE3\u88683\u6642\u70B9")}</dt>
                    <dd>{publicText("Open\u7387 36% \u2192 61% \u2192 96%")}</dd>
                  </div>
                  <div>
                    <dt>{publicText("\u5149\u5408\u6210")}</dt>
                    <dd>{publicText("Open\u7387\u306E\u4E0A\u6607\u306B\u4F34\u3044\u3001\u5149\u5408\u6210\u901F\u5EA6\u3082\u4E0A\u6607\u3059\u308B\u50BE\u5411")}</dd>
                  </div>
                  <div>
                    <dt>{publicText("\u958B\u5EA6\u5206\u5E03")}</dt>
                    <dd>{publicText("\u663C\u306B\u5411\u3051\u3066\u5206\u5E03\u5168\u4F53\u304C\u5927\u304D\u3044\u958B\u5EA6\u5074\u3078\u30B7\u30D5\u30C8")}</dd>
                  </div>
                </dl>

                <a href={CASE03.images.flyerPdf} target="_blank" rel="noreferrer" className="science-button is-primary case03-flyer-button">{publicText("\u65E5\u672C\u8A9E\u30C1\u30E9\u30B7PDF\u3092\u958B\u304F")}</a>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="science-section">
          <div className="case-container">
            <SectionHeader index="08" eyebrow="ENVIRONMENTAL CONTEXT" title={publicText("気温、湿度、VPD、CO₂も同時に変化。")} description={publicText("気孔と光合成を解釈する際は、光だけでなく蒸散要求やCO₂環境も併記する必要があります。")} />

            <div className="science-chart-grid">
              <Reveal>
                <ScientificLineChart labels={labels} series={[{
                name: "気温",
                values: values("temperature")
              }]} title={publicText("気温")} subtitle={publicText("時間帯平均")} unit="°C" yMin={0} yMax={40} valueDigits={1} />
              </Reveal>
              <Reveal delay={80}>
                <ScientificLineChart labels={labels} series={[{
                name: "VPD",
                values: values("vpd")
              }]} title={publicText("VPD")} subtitle={publicText("時間帯平均")} unit="kPa" yMin={0} yMax={4.5} valueDigits={2} />
              </Reveal>
              <Reveal delay={160}>
                <ScientificLineChart labels={labels} series={[{
                name: "相対湿度",
                values: values("rh")
              }]} title={publicText("相対湿度")} subtitle={publicText("時間帯平均")} unit="%RH" yMin={0} yMax={80} valueDigits={1} />
              </Reveal>
              <Reveal delay={240}>
                <ScientificLineChart labels={labels} series={[{
                name: "CO₂",
                values: values("co2")
              }]} title={publicText("CO₂濃度")} subtitle={publicText("時間帯平均")} unit="ppm" yMin={350} yMax={1000} valueDigits={0} />
              </Reveal>
            </div>
          </div>
        </section>

        <section className="science-section science-results-section">
          <div className="case-container">
            <SectionHeader index="09" eyebrow="INTEGRATED DATA TABLE" title={publicText("気孔・光合成・環境の1時間統合値。")} description={publicText("Open率の列名に集計単位を明示し、チラシの代表3時点値も別列で示します。")} />

            <DataTable caption={publicText("Table 2. 6時台〜12時台 気孔・環境・光合成統合データ")} columns={[{
            key: "timeLabel",
            label: "時間帯"
          }, {
            key: "leaves",
            label: "有効葉数",
            align: "right"
          }, {
            key: "stomata",
            label: "気孔数",
            align: "right",
            render: v => formatNumber(v, 0)
          }, {
            key: "leafMeanOpenRateText",
            label: "葉単位Open率",
            unit: "%",
            align: "right"
          }, {
            key: "pooledOpenRateText",
            label: "気孔数ベースOpen率",
            unit: "%",
            align: "right"
          }, {
            key: "allMedianText",
            label: "全気孔中央値",
            unit: "µm",
            align: "right"
          }, {
            key: "openMedianText",
            label: "Openのみ中央値",
            unit: "µm",
            align: "right"
          }, {
            key: "ppfdText",
            label: "PPFD",
            unit: "µmol m⁻² s⁻¹",
            align: "right"
          }, {
            key: "photosynthesisText",
            label: "光合成 A'",
            align: "right"
          }, {
            key: "gasN",
            label: "光合成 n",
            align: "right"
          }, {
            key: "temperatureText",
            label: "気温",
            unit: "°C",
            align: "right"
          }, {
            key: "rhText",
            label: "RH",
            unit: "%",
            align: "right"
          }, {
            key: "vpdText",
            label: "VPD",
            unit: "kPa",
            align: "right"
          }, {
            key: "co2Text",
            label: "CO₂",
            unit: "ppm",
            align: "right"
          }]} rows={tableRows} />

          </div>
        </section>

        <section className="science-section">
          <div className="study-content">
            <SectionHeader index="10" eyebrow="INTERPRETATION" title={publicText("大量計測の価値は、平均値だけでなく分布と集計単位を示せること。")} />

            <Reveal className="science-prose-columns">
              <div>
                <h3>{publicText("\u3053\u306E\u30C7\u30FC\u30BF\u304C\u652F\u6301\u3059\u308B\u8A18\u8FF0")}</h3>
                <ul>
                  <li>{publicText("\u671D\u304B\u3089\u663C\u306B\u5411\u3051\u3066Open\u7387\u306F\u4E0A\u6607\u3057\u305F\u3002")}</li>
                  <li>{publicText("6\u30FB9\u30FB12\u6642\u53F0\u306E\u6C17\u5B54\u6570\u30D9\u30FC\u30B9Open\u7387\u306F36.2\u300161.5\u300196.2%\u3060\u3063\u305F\u3002")}</li>
                  <li>{publicText("\u671D\u524D\u534A\u306FOpen\u306B\u306A\u308B\u6C17\u5B54\u304C\u5897\u3048\u308B\u5909\u5316\u304C\u5927\u304D\u304B\u3063\u305F\u3002")}</li>
                  <li>{publicText("\u663C\u306B\u5411\u3051\u3066\u958B\u5EA6\u5206\u5E03\u306F\u5927\u304D\u3044\u5074\u3078\u79FB\u52D5\u3057\u305F\u3002")}</li>
                  <li>{publicText("\u5149\u5408\u6210\u901F\u5EA6\u3082\u540C\u3058\u6642\u9593\u8EF8\u3067\u4E0A\u6607\u3059\u308B\u50BE\u5411\u3092\u793A\u3057\u305F\u3002")}</li>
                </ul>
              </div>
              <div>
                <h3>{publicText("\u89E3\u91C8\u6642\u306B\u660E\u793A\u3059\u308B\u70B9")}</h3>
                <ul>
                  <li>{publicText("\u8449\u5358\u4F4DOpen\u7387\u3068\u6C17\u5B54\u6570\u30D9\u30FC\u30B9Open\u7387\u306F\u7570\u306A\u308B\u96C6\u8A08\u3002")}</li>
                  <li>{publicText("\u540C\u4E00\u8449\u306E\u9023\u7D9A\u8FFD\u8DE1\u3067\u306F\u306A\u304F\u3001\u5404\u6642\u9593\u5E2F\u3067\u7570\u306A\u308B\u8449\u3092\u30B5\u30F3\u30D7\u30EA\u30F3\u30B0\u3002")}</li>
                  <li>{publicText("\u6642\u9593\u5E2F\u3054\u3068\u306E\u8449\u6570\u30FB\u6C17\u5B54\u6570\u306F\u5747\u4E00\u3067\u306F\u306A\u3044\u3002")}</li>
                  <li>{publicText("\u5149\u30FB\u6E29\u6E7F\u5EA6\u30FBVPD\u30FBCO\u2082\u304C\u540C\u6642\u306B\u5909\u5316\u3057\u3066\u3044\u308B\u3002")}</li>
                  <li>{publicText("296\u8449\u3068\u7D71\u5408\u8868295\u8449\u306E1\u8449\u5DEE\u306F\u5143\u30C7\u30FC\u30BF\u78BA\u8A8D\u304C\u5FC5\u8981\u3002")}</li>
                </ul>
              </div>
            </Reveal>

            <ScientificNote title={publicText("専門的な次の解析")} tone="success">
              <p>{publicText("\u8449\u3092\u5206\u6790\u5358\u4F4D\u3068\u3057\u305F\u4E8C\u9805\u30E2\u30C7\u30EB\u3067Open\u78BA\u7387\u3092\u3001Open\u6C17\u5B54\u306E\u307F\u3092\u5BFE\u8C61\u3068\u3057\u305F \u9023\u7D9A\u5FDC\u7B54\u30E2\u30C7\u30EB\u3067\u958B\u5EA6\u3092\u8A55\u4FA1\u3057\u3001\u6642\u523B\u30FBPPFD\u30FBVPD\u30FBCO\u2082\u3068\u306E\u95A2\u4FC2\u3092 \u968E\u5C64\u7684\u306B\u89E3\u6790\u3059\u308B\u3068\u3001\u690D\u7269\u7FA4\u306E\u4E8C\u6BB5\u968E\u5FDC\u7B54\u3092\u3088\u308A\u53B3\u5BC6\u306B\u691C\u8A3C\u3067\u304D\u307E\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section className="science-case-conclusion">
          <div className="study-content">
            <Reveal>
              <RiseOutlined />
              <span>{publicText("HIGH-THROUGHPUT FIELD PHYSIOLOGY")}</span>
              <h2>{publicText("\u5927\u91CF\u306B\u6E2C\u5B9A\u3067\u304D\u308B\u304B\u3089\u3001\u7814\u7A76\u306E\u554F\u3044\u3092\u3082\u3063\u3068\u6DF1\u304F\u3067\u304D\u308B\u3002")}</h2>
              <p>{publicText("14,563\u6C17\u5B54\u3092\u3001Open\u7387\u30FB\u958B\u5EA6\u5206\u5E03\u30FB\u5149\u5408\u6210\u30FB\u74B0\u5883\u3068\u540C\u3058\u6642\u9593\u8EF8\u3067\u8AAD\u3080\u3002 StomataScope\u306F\u3001\u5546\u696D\u683D\u57F9\u5703\u5834\u306B\u304A\u3051\u308B\u690D\u7269\u7FA4\u306E\u5909\u5316\u3092\u3001 \u5358\u4E00\u306E\u5E73\u5747\u5024\u3067\u306F\u306A\u304F\u591A\u5C64\u306E\u30C7\u30FC\u30BF\u3068\u3057\u3066\u8A18\u9332\u3057\u307E\u3059\u3002")}</p>
            </Reveal>
          </div>
        </section>

        <div className="case-container">
          <CaseNavigation previous={{
          id: "02",
          path: "/case-studies/02",
          title: "乾くと、気孔応答の違いが見えてきた。"
        }} next={{
          id: "04",
          path: "/case-studies/04",
          title: "18,582個の気孔から、何が見える？"
        }} />
        </div>
      </article>
    </PublicLayout>;
}
export default CaseStudy03;
